<template>
  <div class="employee-form">
    <h3 class="employee-form__title">Общие данные</h3>

    <div class="app-input-group employee-form__item">
      <app-input-control
        placeholder="Иванов"
        title="Фамилия"
        tooltip="Укажите фамилию"
        isRequired
        v-model="employForm.lastName"
      ></app-input-control>
      <small
        v-if="$v.employForm.lastName.$dirty && $v.employForm.lastName.$error"
        class="app-input-group__invalid"
      >
        Введите фамилию
      </small>
    </div>

    <div class="app-input-group employee-form__item">
      <app-input-control
        placeholder="Иван"
        title="Имя"
        tooltip="Укажите имя"
        isRequired
        v-model="employForm.firstName"
      ></app-input-control>
      <small
        v-if="$v.employForm.firstName.$dirty && $v.employForm.firstName.$error"
        class="app-input-group__invalid"
      >
        Введите имя
      </small>
    </div>

    <div class="app-input-group employee-form__item">
      <app-input-control
        placeholder="Иванович"
        title="Отчество"
        tooltip="Укажите отчество"
        v-model="employForm.middleName"
      ></app-input-control>
    </div>

    <div class="app-input-group employee-form__item">
      <app-select-control
        title="Пол"
        label="label"
        track-by="label"
        is-tooltip
        tooltip="Выберите пол"
        v-model="genderOption"
        :options="$options.GENDER_OPTIONS"
        :allowEmpty="false"
      />
    </div>

    <div class="app-input-group employee-form__item employee-form__item--col6">
      <app-autocomplete
        title="Должность"
        tooltip="Укажите должность"
        isRequired
        placeholder="Укажите должность"
        :suggests="positionNameOptions"
        v-model="employForm.position.name"
      ></app-autocomplete>
      <small
        v-if="
          $v.employForm.position.name.$dirty &&
          $v.employForm.position.name.$error
        "
        class="app-input-group__invalid"
      >
        Укажите должность
      </small>
    </div>

    <div class="app-input-group employee-form__item employee-form__item--col6">
      <app-calendar-field
        :maxDate="new Date()"
        placeholder="01.01.2021"
        title="Дата вступления в должность"
        tooltip="Укажите дату вступления в должность"
        v-model="employForm.position.activeFrom"
      ></app-calendar-field>

      <small
        v-if="
          $v.employForm.position.activeFrom.$dirty &&
          $v.employForm.position.activeFrom.$error
        "
        class="app-input-group__invalid"
      >
        Укажите дату вступления в должность
      </small>
    </div>

    <h3 class="employee-form__title col-12">Основание полномочий</h3>

    <div class="app-input-group employee-form__item">
      <app-autocomplete
        placeholder="Тип документа"
        title="Тип документа"
        tooltip="Укажите тип документа"
        isRequired
        :suggests="positionCredentialsOptions"
        v-model="employForm.position.credentials.type"
      ></app-autocomplete>
      <small
        v-if="
          $v.employForm.position.credentials.type.$dirty &&
          $v.employForm.position.credentials.type.$error
        "
        class="app-input-group__invalid"
      >
        Укажите тип документа
      </small>
    </div>

    <div class="app-input-group employee-form__item employee-form__item--col6">
      <app-input-control
        placeholder="Номер документа"
        title="Номер документа"
        tooltip="Укажите номер документа"
        v-model="employForm.position.credentials.number"
      ></app-input-control>
    </div>

    <div class="app-input-group employee-form__item employee-form__item--col6">
      <app-calendar-field
        placeholder="Дата документа"
        title="Дата документа"
        tooltip="Укажите дату документа"
        v-model="employForm.position.credentials.date"
      ></app-calendar-field>
      <small
        v-if="
          $v.employForm.position.credentials.date.$dirty &&
          $v.employForm.position.credentials.date.$error
        "
        class="app-input-group__invalid"
      >
        Укажите дату
      </small>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import defaultsDeep from 'lodash/defaultsDeep';
import { GENDER_OPTIONS } from '@/helpers/enums/contractors';
import { required } from 'vuelidate/lib/validators';
import { SuggestionsApi } from '@/api/SuggestionsApi';

export default {
  name: 'EmployeeForm',
  validations: {
    employForm: {
      firstName: { required },
      lastName: { required },
      position: {
        name: { required },
        activeFrom: { required },
        credentials: {
          type: { required },
          date: { required },
        },
      },
    },
  },
  components: {
    AppInputControl: () => import('@/components/ui/AppInputControl'),
    AppSelectControl: () => import('@/components/ui/AppSelectControl'),
    AppCalendarField: () => import('@/components/ui/AppCalendarField'),
    AppAutocomplete: () => import('@/components/ui/AppAutocomplete'),
  },
  props: {
    employeeData: {
      type: Object,
      default() {
        return {};
      },
    },
    typeContractor: {
      type: String,
      default: '',
    },
  },
  data: (vm) => ({
    genderOption: {},
    employForm: vm.createDefaultData(),
    positionName: '',
    positionCredentials: '',
    positionNameValue: '',
    suggestions: {
      positionCompany: [],
      positionEntrepreneur: [],
      credentialsType: [],
    },
  }),
  computed: {
    positionNameOptions() {
      switch (this.typeContractor) {
        case 'company':
          return this.suggestions.positionCompany;
        case 'entrepreneur':
          return this.suggestions.positionEntrepreneur;
        default:
          return [];
      }
    },
    positionCredentialsOptions() {
      return this.suggestions.credentialsType;
    },
  },
  watch: {
    genderOption(value) {
      this.employForm.gender = value.gender;
    },
  },
  async created() {
    this.$options.GENDER_OPTIONS = GENDER_OPTIONS;
    const innerData = cloneDeep(this.employeeData);
    defaultsDeep(innerData, this.employForm);
    this.employForm = innerData;
    this.genderOption = GENDER_OPTIONS.find(
      (el) => el.gender === this.employForm.gender
    );
    this.suggestions = await new SuggestionsApi().getSuggestions(
      'positionCompany,positionEntrepreneur,credentialsType'
    );
  },
  methods: {
    /**
     * Отправка данных на верх
     */
    handlerSubmit() {
      if (this.$v.$invalid) {
        this.$v.$touch();
        this.$emit('validate', false);
        return false;
      }
      const formData = cloneDeep(this.employForm);
      this.$emit('submit', formData);
      return formData;
    },
    /**
     * Дефолтные значения формы
     */
    createDefaultData() {
      return {
        firstName: '',
        lastName: '',
        middleName: '',
        gender: 'M',
        position: {
          name: '',
          activeFrom: new Date(),
          credentials: {
            type: '',
            number: '',
            date: new Date(),
          },
        },
      };
    },
    /**
     * Очистка формы
     */
    clearForm() {
      this.employForm = this.createDefaultData();
      return this.employForm;
    },
  },
};
</script>

<style lang="scss" scoped>
.employee-form {
  @include grid-in(15px);

  &__title {
    @include col(12, 15px);

    margin-top: 0;
  }

  &__item {
    @include col(12, 15px);

    &--col6 {
      @include col(6, 15px);
      @include col-xs(12, 15px);
    }

    &--col8 {
      @include col(8, 15px);
      @include col-xs(12, 15px);
    }

    &--col4 {
      @include col(4, 15px);
      @include col-xs(12, 15px);
    }

    margin-bottom: 15px;
  }
}
</style>
